import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  server_post_data,
  seo_data_url,
  Website_URL,
} from "./ServiceConnection/serviceconnection.js";

/* ------------------------------------------------------------------ */
/* Analytics helper (GA4 via gtag loaded in index.html)                */
/* ------------------------------------------------------------------ */
export const trackEvent = (name, params = {}) => {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", name, params);
    }
  } catch (e) {}
};

const SITE = (Website_URL || "https://www.raghuvanshihall.com/").replace(/\/+$/, "");

const setMeta = (selector, attr, value) => {
  if (!value) return;
  const el = document.head.querySelector(selector);
  if (el) el.setAttribute(attr, value);
};

/**
 * Runs on every client-side route change:
 *  - updates <title>, description, canonical and OG/Twitter tags from the SEO API
 *    (the first page load already has them from server.js)
 *  - sends a GA4 page_view (gtag only sends one automatically on first load)
 *  - fires generate_lead when the thank-you page is reached
 * Also tracks clicks on phone / WhatsApp / email links site-wide.
 */
export default function RouteEffects() {
  const { pathname, search } = useLocation();
  const firstLoad = useRef(true);

  // Route-change effects
  useEffect(() => {
    if (pathname.toLowerCase().startsWith("/thank-you")) {
      trackEvent("generate_lead", { form: "lead_form" });
    }

    if (firstLoad.current) {
      firstLoad.current = false;
      return;
    }
    // server-painted home hero is only for the first paint of "/"
    const pre = document.getElementById("rh-hero-pre");
    if (pre) pre.remove();

    let cancelled = false;
    const canonical = SITE + (pathname === "/" ? "/" : pathname);

    const sendPageView = () =>
      trackEvent("page_view", {
        page_path: pathname + search,
        page_location: window.location.href,
        page_title: document.title,
      });

    (async () => {
      try {
        const fd = new FormData();
        fd.append("current_url", pathname);
        const res = await server_post_data(seo_data_url, fd);
        const row =
          res &&
          res.data &&
          !res.data.error &&
          res.data.message &&
          res.data.message.seo_loop &&
          res.data.message.seo_loop[0];
        if (cancelled) return;
        if (row) {
          if (row.title_name) document.title = row.title_name;
          setMeta('meta[name="description"]', "content", row.description);
          setMeta('meta[property="og:description"]', "content", row.description);
          setMeta('meta[property="twitter:description"]', "content", row.description);
          setMeta('meta[name="title"]', "content", row.title_name);
          setMeta('meta[property="og:title"]', "content", row.title_name);
          setMeta('meta[property="twitter:title"]', "content", row.title_name);
          setMeta('meta[name="keywords"]', "content", row.keywords);
        }
        const url = (row && row.canonical_url) || canonical;
        setMeta('link[rel="canonical"]', "href", url);
        setMeta('meta[property="og:url"]', "content", url);
        setMeta('meta[property="twitter:url"]', "content", url);
      } catch (e) {
        setMeta('link[rel="canonical"]', "href", canonical);
      } finally {
        if (!cancelled) sendPageView();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pathname, search]);

  // Click tracking (delegated, one listener for the whole app)
  useEffect(() => {
    const onClick = (e) => {
      const a = e.target.closest && e.target.closest("a, button, [data-track]");
      if (!a) return;
      const placement = a.getAttribute("data-track-placement") || pathnameOf();
      const custom = a.getAttribute("data-track");
      const href = (a.getAttribute("href") || "").toLowerCase();

      if (href.startsWith("tel:")) {
        trackEvent("call_click", { placement });
      } else if (href.includes("wa.me") || href.includes("whatsapp.com")) {
        trackEvent("whatsapp_click", { placement });
      } else if (href.startsWith("mailto:")) {
        trackEvent("email_click", { placement });
      } else if (href.includes("google.com/maps") || href.includes("maps.google")) {
        trackEvent("directions_click", { placement });
      } else if (custom) {
        trackEvent(custom, { placement });
      }
    };
    const pathnameOf = () => window.location.pathname;
    document.addEventListener("click", onClick, { passive: true });
    return () => document.removeEventListener("click", onClick);
  }, []);

  return null;
}
